import { FC } from 'react'
import Button from '@/components/Button/Button'
import useI18n from '@/hooks/use-i18n'
import { FormData, FormErrors } from '@/hooks/useNewsletterForm'
import classNames from 'classnames'

interface NewsletterFormFieldsProps {
  formData: FormData
  errors: FormErrors
  loading: boolean
  locations: string[]
  handleChange: (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  handlePhoneChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  handleDateChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  className?: string
  inputClassName?: string
  selectClassName?: string
  rowClassName?: string
  formFieldClassName?: string
  checkboxRowClassName?: string
  checkboxLabelClassName?: string
  checkboxClassName?: string
  checkboxTextClassName?: string
  submitButtonClassName?: string
  errorClassName?: string
  nameRowClassName?: string
}

export const NewsletterFormFields: FC<NewsletterFormFieldsProps> = ({
  formData,
  errors,
  loading,
  locations,
  handleChange,
  handlePhoneChange,
  handleDateChange,
  handleCheckboxChange,
  inputClassName,
  selectClassName,
  rowClassName,
  formFieldClassName,
  checkboxRowClassName,
  checkboxLabelClassName,
  checkboxClassName,
  checkboxTextClassName,
  submitButtonClassName,
  errorClassName,
  nameRowClassName,
}) => {
  const { i18n } = useI18n()

  return (
    <>
      <div className={classNames(rowClassName, nameRowClassName)}>
        <div className={formFieldClassName}>
          <label htmlFor="firstName" className="sr-only">{i18n('firstName')}</label>
          <input
            id="firstName"
            type="text"
            name="firstName"
            placeholder={i18n('firstName')}
            value={formData.firstName}
            onChange={handleChange('firstName')}
            className={inputClassName}
            required
          />
          {errors.firstName && <span className={errorClassName}>{errors.firstName}</span>}
        </div>
        <div className={formFieldClassName}>
          <label htmlFor="lastName" className="sr-only">{i18n('lastName')}</label>
          <input
            id="lastName"
            type="text"
            name="lastName"
            placeholder={i18n('lastName')}
            value={formData.lastName}
            onChange={handleChange('lastName')}
            className={inputClassName}
            required
          />
          {errors.lastName && <span className={errorClassName}>{errors.lastName}</span>}
        </div>
      </div>

      <div className={rowClassName}>
        <div className={formFieldClassName}>
          <label htmlFor="email" className="sr-only">{i18n('emailAddress')}</label>
          <input
            id="email"
            type="email"
            name="email"
            placeholder={i18n('emailAddress')}
            value={formData.email}
            onChange={handleChange('email')}
            className={inputClassName}
            required
          />
          {errors.email && <span className={errorClassName}>{errors.email}</span>}
        </div>
        <div className={formFieldClassName}>
          <label htmlFor="phone" className="sr-only">{i18n('phoneNumber')}</label>
          <input
            id="phone"
            type="tel"
            name="phone"
            placeholder={i18n('phoneNumber')}
            value={formData.phone}
            onChange={handlePhoneChange}
            className={inputClassName}
            required
            pattern="^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$"
            aria-describedby="phone-format"
          />
          {errors.phone && <span className={errorClassName} id="phone-format">{errors.phone}</span>}
        </div>
      </div>

      <div className={rowClassName}>
        <div className={formFieldClassName}>
          <label htmlFor="birthday" className="sr-only">{i18n('birthday')}</label>
          <input
            id="birthday"
            type="text"
            pattern="(0[1-9]|1[0-2])/(0[1-9]|[12][0-9]|3[01])/[0-9]{4}"
            onFocus={(e) => {
              if (e.target.type === 'text') {
                e.target.type = 'text'
                e.target.placeholder = 'MM/DD/YYYY *'
              }
            }}
            onBlur={(e) => {
              if (!e.target.value) {
                e.target.type = 'text'
                e.target.placeholder = `${i18n('birthday')} *` || 'Birthday *'
              }
            }}
            name="birthday"
            value={formData.birthday}
            onChange={handleDateChange}
            className={inputClassName}
            required
            placeholder={`${i18n('birthday')} *` || 'Birthday *'}
            maxLength={10}
            inputMode="numeric"
          />
          {errors.birthday && <span className={errorClassName}>{errors.birthday}</span>}
        </div>
        <div className={formFieldClassName}>
          <label htmlFor="location" className="sr-only">{i18n('preferredLocation')}</label>
          <select
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange('location')}
            className={selectClassName}
            required
          >
            <option value="">{`${i18n('preferredLocation')} *`}</option>
            {locations.map((location, index) => (
              <option key={index} value={location}>
                {location}
              </option>
            ))}
          </select>
          {errors.location && <span className={errorClassName}>{errors.location}</span>}
        </div>
      </div>

      <div className={checkboxRowClassName}>
        <label className={checkboxLabelClassName}>
          <input
            type="checkbox"
            name="optInToTexts"
            checked={formData.optInToTexts}
            onChange={handleCheckboxChange}
            className={checkboxClassName}
          />
          <span className={checkboxTextClassName}>
            Sign up for texts and get updates, offers, and more from Din Tai Fung. Msg & data rates may apply. Text STOP to opt out anytime.
          </span>
        </label>
      </div>

      <Button
        className={submitButtonClassName}
        type="submit"
        primary
        icon="caretRight"
        label={loading ? i18n('pleaseWait') : i18n('submit')}
        disabled={loading}
      />
    </>
  )
}
